import { Link, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { NotificationRow } from "@/components/notifications/notification-row"
import { NotificationsEmptyState } from "@/components/notifications/notifications-empty-state"
import { NotificationsLoadingSkeletons } from "@/components/notifications/notifications-loading-skeletons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { markAllReadNotificationsPath, notificationsPath } from "@/routes"
import type { NotificationRow as NotificationRowType } from "@/types/notifications"

interface NotificationsPanelProps {
  notifications: NotificationRowType[] | null
  unreadCount: number
  isLoading: boolean
  /** Called when the row is clicked — the panel can then close itself. */
  onRowMarkRead?: () => void
  className?: string
}

/**
 * OKS-36 — Panel content shell shared by the desktop popover and the
 * mobile sheet (BellTrigger picks the wrapper based on viewport).
 *
 * Composition (UX Spec §2):
 *   header — title + Mark all read (disabled when unread === 0)
 *   list   — scrollable, max-h-[480px] on desktop popover; full-height
 *            in the mobile sheet (parent controls the height there)
 *   footer — "View all" → /notifications
 *
 * "Mark all read" is optimistic per UX Spec §4: button disables + rows
 * fade unread→read on click, before any round-trip. Inertia's partial
 * reload of `unread_notifications_count` reconciles the bell badge on
 * success. Failure reverts both overlays and surfaces a destructive
 * toast.
 */
export function NotificationsPanel({
  notifications,
  unreadCount,
  isLoading,
  onRowMarkRead,
  className,
}: NotificationsPanelProps) {
  const { t } = useTranslation()
  const [optimisticAllRead, setOptimisticAllRead] = useState(false)
  const [optimisticReadIds, setOptimisticReadIds] = useState<Set<number>>(
    () => new Set(),
  )

  const hasUnread = !optimisticAllRead && unreadCount > 0

  function handleMarkAllRead() {
    if (notifications) {
      setOptimisticReadIds(
        new Set(notifications.filter((n) => !n.read_at).map((n) => n.id)),
      )
    }
    setOptimisticAllRead(true)

    router.post(
      markAllReadNotificationsPath(),
      {},
      {
        preserveScroll: true,
        onError: () => {
          setOptimisticAllRead(false)
          setOptimisticReadIds(new Set())
          toast.error(t("notifications.error.mark_all_read"))
        },
        onSuccess: () => {
          // Round-trip reconciled: server-truth `read_at` will arrive
          // via the next `recent_notifications` partial reload; drop
          // overlays so we don't double-account.
          setOptimisticAllRead(false)
          setOptimisticReadIds(new Set())
        },
      },
    )
  }

  const overlaidRows: NotificationRowType[] | null = notifications
    ? notifications.map((n) =>
        optimisticReadIds.has(n.id) && !n.read_at
          ? { ...n, read_at: new Date().toISOString() }
          : n,
      )
    : null

  return (
    <div
      data-testid="notifications-panel"
      className={cn("flex h-full flex-col", className)}
    >
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold">
          {t("notifications.panel.title")}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!hasUnread}
          onClick={handleMarkAllRead}
          data-testid="notifications-panel-mark-all-read"
        >
          {t("notifications.panel.mark_all_read")}
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto sm:max-h-[480px]">
        {isLoading || overlaidRows === null ? (
          <NotificationsLoadingSkeletons />
        ) : overlaidRows.length === 0 ? (
          <NotificationsEmptyState compact />
        ) : (
          <ul className="flex flex-col">
            {overlaidRows.map((n) => (
              // Key on read state: when the optimistic overlay flips
              // read_at, NotificationRow's internal `useState` (init
              // from prop) needs to remount to pick up the change.
              <li key={`${n.id}-${n.read_at ? "r" : "u"}`}>
                <NotificationRow
                  notification={n}
                  variant="panel"
                  onMarkRead={onRowMarkRead}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="border-t px-4 py-2 text-center">
        <Link
          href={notificationsPath()}
          data-testid="notifications-panel-view-all"
          className="text-primary text-xs font-medium hover:underline"
        >
          {t("notifications.panel.view_all")}
        </Link>
      </footer>
    </div>
  )
}
